import { useCallback, useEffect, useRef, useState } from 'react';
import { clientPointToLatLng, latLngToClientPoint } from '@/lib/google-maps';
import { PALETTE_DROP_PADDING_PX } from './constants';
import type { ClientPoint } from './types';
import { isPointerOverElement } from './utils';

function getDomEventClientPoint(
  domEvent: google.maps.MapMouseEvent['domEvent'],
): ClientPoint | null {
  if (!domEvent || !('clientX' in domEvent) || !('clientY' in domEvent)) {
    return null;
  }

  return { x: domEvent.clientX, y: domEvent.clientY };
}

interface PaletteDragListeners {
  onMove: (event: PointerEvent) => void;
  onUp: (event: PointerEvent) => void;
  onCancel: (event: PointerEvent) => void;
}

interface UseMapDragInteractionsOptions {
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  mapWrapperRef: React.RefObject<HTMLDivElement | null>;
  paletteRef: React.RefObject<HTMLDivElement | null>;
  paletteButtonRef: React.RefObject<HTMLButtonElement | null>;
  readOnly: boolean;
  canDragFromPalette: boolean;
  showMapMarker: boolean;
  resolveCoordinates: (lat: number, lng: number) => Promise<void>;
  clearLocation: () => void;
}

export function useMapDragInteractions({
  mapRef,
  mapWrapperRef,
  paletteRef,
  paletteButtonRef,
  readOnly,
  canDragFromPalette,
  showMapMarker,
  resolveCoordinates,
  clearLocation,
}: UseMapDragInteractionsOptions) {
  const isOverPaletteRef = useRef(false);
  const isDraggingMarkerRef = useRef(false);
  const isDraggingFromPaletteRef = useRef(false);
  const isPlacingFromPaletteRef = useRef(false);
  const paletteDragOverMapRef = useRef(false);
  const paletteDragFinishedRef = useRef(false);
  const activePalettePointerIdRef = useRef<number | null>(null);
  const paletteDragCleanupRef = useRef<PaletteDragListeners | null>(null);
  const finishPaletteDragRef = useRef<
    | ((clientX: number, clientY: number, pointerId?: number) => Promise<void>)
    | null
  >(null);
  const cancelPaletteDragRef = useRef<((pointerId?: number) => void) | null>(
    null,
  );
  const updatePaletteDragTargetRef = useRef<
    ((clientX: number, clientY: number) => boolean) | null
  >(null);

  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<ClientPoint | null>(null);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [markerDragPosition, setMarkerDragPosition] =
    useState<ClientPoint | null>(null);
  const [isOverMap, setIsOverMap] = useState(false);
  const [isOverPalette, setIsOverPalette] = useState(false);

  const waitForMapIdle = useCallback((map: google.maps.Map) => {
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(fallbackTimer);
        resolve();
      };

      google.maps.event.addListenerOnce(map, 'idle', finish);
      const fallbackTimer = window.setTimeout(finish, 200);
    });
  }, []);

  const placeCoordinates = useCallback(
    async (clientX: number, clientY: number) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }

      await waitForMapIdle(map);
      const latLng = await clientPointToLatLng(map, clientX, clientY);
      await resolveCoordinates(latLng.lat(), latLng.lng());
    },
    [mapRef, resolveCoordinates, waitForMapIdle],
  );

  const isOverPaletteAtPoint = useCallback(
    (clientX: number, clientY: number) => {
      return isPointerOverElement(
        paletteRef.current,
        clientX,
        clientY,
        PALETTE_DROP_PADDING_PX,
      );
    },
    [paletteRef],
  );

  const isOverMapAtPoint = useCallback(
    (clientX: number, clientY: number) => {
      if (isOverPaletteAtPoint(clientX, clientY)) {
        return false;
      }

      const mapDiv = mapRef.current?.getDiv();
      if (mapDiv && isPointerOverElement(mapDiv, clientX, clientY)) {
        return true;
      }

      return isPointerOverElement(mapWrapperRef.current, clientX, clientY);
    },
    [isOverPaletteAtPoint, mapRef, mapWrapperRef],
  );

  const updatePaletteDragTarget = useCallback(
    (clientX: number, clientY: number) => {
      const overMap = isOverMapAtPoint(clientX, clientY);
      paletteDragOverMapRef.current = overMap;
      setIsOverMap(overMap);
      return overMap;
    },
    [isOverMapAtPoint],
  );

  const releasePalettePointerCapture = useCallback(
    (pointerId: number) => {
      const button = paletteButtonRef.current;
      if (button?.hasPointerCapture(pointerId)) {
        button.releasePointerCapture(pointerId);
      }
    },
    [paletteButtonRef],
  );

  const removePaletteDragListeners = useCallback(() => {
    const listeners = paletteDragCleanupRef.current;
    if (!listeners) {
      return;
    }

    window.removeEventListener('pointermove', listeners.onMove, true);
    window.removeEventListener('pointerup', listeners.onUp, true);
    window.removeEventListener('pointercancel', listeners.onCancel, true);
    paletteDragCleanupRef.current = null;
    activePalettePointerIdRef.current = null;
  }, []);

  const resetPaletteDragUi = useCallback(() => {
    isDraggingFromPaletteRef.current = false;
    paletteDragOverMapRef.current = false;
    setIsDragging(false);
    setDragPosition(null);
    setIsOverMap(false);
  }, []);

  const finishPaletteDrag = useCallback(
    async (clientX: number, clientY: number, pointerId?: number) => {
      if (!isDraggingFromPaletteRef.current || paletteDragFinishedRef.current) {
        return;
      }

      paletteDragFinishedRef.current = true;
      removePaletteDragListeners();
      if (pointerId != null) {
        releasePalettePointerCapture(pointerId);
      }
      resetPaletteDragUi();

      const overMapAtDrop = isOverMapAtPoint(clientX, clientY);
      const overMap = paletteDragOverMapRef.current || overMapAtDrop;
      if (!overMap) {
        return;
      }

      isPlacingFromPaletteRef.current = true;
      try {
        await placeCoordinates(clientX, clientY);
      } finally {
        isPlacingFromPaletteRef.current = false;
      }
    },
    [
      isOverMapAtPoint,
      placeCoordinates,
      releasePalettePointerCapture,
      removePaletteDragListeners,
      resetPaletteDragUi,
    ],
  );

  const cancelPaletteDrag = useCallback(
    (pointerId?: number) => {
      if (!isDraggingFromPaletteRef.current) {
        return;
      }

      removePaletteDragListeners();
      if (pointerId != null) {
        releasePalettePointerCapture(pointerId);
      }
      resetPaletteDragUi();
    },
    [
      releasePalettePointerCapture,
      removePaletteDragListeners,
      resetPaletteDragUi,
    ],
  );

  finishPaletteDragRef.current = finishPaletteDrag;
  cancelPaletteDragRef.current = cancelPaletteDrag;
  updatePaletteDragTargetRef.current = updatePaletteDragTarget;

  const updatePaletteDropTarget = useCallback(
    (clientX: number, clientY: number) => {
      const overPalette = isOverPaletteAtPoint(clientX, clientY);
      isOverPaletteRef.current = overPalette;
      setIsOverPalette(overPalette);
      return overPalette;
    },
    [isOverPaletteAtPoint],
  );

  const markerDroppedOnPalette = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (isOverPaletteRef.current) {
        return true;
      }

      const domEvent = event.domEvent;
      const point = getDomEventClientPoint(domEvent);
      return Boolean(point && isOverPaletteAtPoint(point.x, point.y));
    },
    [isOverPaletteAtPoint],
  );

  const handleMarkerDragStart = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (readOnly || !showMapMarker) {
        return;
      }
      isDraggingMarkerRef.current = true;
      isOverPaletteRef.current = false;
      setIsOverPalette(false);
      setIsDraggingMarker(true);

      const domEvent = event.domEvent;
      const point = getDomEventClientPoint(domEvent);
      if (point) {
        setMarkerDragPosition(point);
      }
    },
    [readOnly, showMapMarker],
  );

  const handleMarkerDrag = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (readOnly || !isDraggingMarkerRef.current) {
        return;
      }

      const domEvent = event.domEvent;
      const point = getDomEventClientPoint(domEvent);
      if (point) {
        setMarkerDragPosition(point);
        updatePaletteDropTarget(point.x, point.y);
        return;
      }

      const map = mapRef.current;
      if (!map || !event.latLng) {
        return;
      }

      latLngToClientPoint(map, event.latLng)
        .then((point) => {
          setMarkerDragPosition(point);
          updatePaletteDropTarget(point.x, point.y);
        })
        .catch(() => {});
    },
    [mapRef, readOnly, updatePaletteDropTarget],
  );

  const handleMarkerDragEnd = useCallback(
    async (event: google.maps.MapMouseEvent) => {
      try {
        if (
          readOnly ||
          !showMapMarker ||
          !event.latLng ||
          !isDraggingMarkerRef.current ||
          isPlacingFromPaletteRef.current
        ) {
          return;
        }

        const droppedOnPalette = markerDroppedOnPalette(event);
        isOverPaletteRef.current = false;
        setIsOverPalette(false);

        if (droppedOnPalette) {
          clearLocation();
          return;
        }

        await resolveCoordinates(event.latLng.lat(), event.latLng.lng());
      } finally {
        isDraggingMarkerRef.current = false;
        setIsDraggingMarker(false);
        setMarkerDragPosition(null);
      }
    },
    [
      clearLocation,
      markerDroppedOnPalette,
      readOnly,
      resolveCoordinates,
      showMapMarker,
    ],
  );

  const handlePalettePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!canDragFromPalette || !mapRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      removePaletteDragListeners();
      paletteDragFinishedRef.current = false;
      isOverPaletteRef.current = false;
      setIsOverPalette(false);
      isPlacingFromPaletteRef.current = false;
      activePalettePointerIdRef.current = event.pointerId;
      isDraggingFromPaletteRef.current = true;
      paletteDragOverMapRef.current = isOverMapAtPoint(
        event.clientX,
        event.clientY,
      );
      setIsDragging(true);
      setDragPosition({ x: event.clientX, y: event.clientY });
      setIsOverMap(paletteDragOverMapRef.current);

      const pointerId = event.pointerId;
      try {
        event.currentTarget.setPointerCapture(pointerId);
      } catch {
        // Pointer capture is best-effort; window listeners handle the drop.
      }

      const onMove = (moveEvent: PointerEvent) => {
        if (
          !isDraggingFromPaletteRef.current ||
          moveEvent.pointerId !== pointerId
        ) {
          return;
        }
        setDragPosition({ x: moveEvent.clientX, y: moveEvent.clientY });
        updatePaletteDragTargetRef.current?.(
          moveEvent.clientX,
          moveEvent.clientY,
        );
      };

      const onUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) {
          return;
        }
        void finishPaletteDragRef.current?.(
          upEvent.clientX,
          upEvent.clientY,
          pointerId,
        );
      };

      const onCancel = (cancelEvent: PointerEvent) => {
        if (cancelEvent.pointerId !== pointerId) {
          return;
        }
        cancelPaletteDragRef.current?.(pointerId);
      };

      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onCancel, true);
      paletteDragCleanupRef.current = { onMove, onUp, onCancel };
    },
    [canDragFromPalette, isOverMapAtPoint, mapRef, removePaletteDragListeners],
  );

  const handlePalettePointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (
        !isDraggingFromPaletteRef.current ||
        event.pointerId !== activePalettePointerIdRef.current
      ) {
        return;
      }
      void finishPaletteDragRef.current?.(
        event.clientX,
        event.clientY,
        event.pointerId,
      );
    },
    [],
  );

  const handlePalettePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.pointerId !== activePalettePointerIdRef.current) {
        return;
      }
      cancelPaletteDragRef.current?.(event.pointerId);
    },
    [],
  );

  useEffect(() => {
    return () => {
      removePaletteDragListeners();
    };
  }, [removePaletteDragListeners]);

  return {
    isDragging,
    dragPosition,
    isDraggingMarker,
    markerDragPosition,
    isOverMap,
    isOverPalette,
    handleMarkerDragStart,
    handleMarkerDrag,
    handleMarkerDragEnd,
    handlePalettePointerDown,
    handlePalettePointerUp,
    handlePalettePointerCancel,
  };
}
